
import React, { useRef, useCallback } from 'react';
import { SystemConfig, Order } from '../types';
import { DistanceService } from '../services/parserService';

interface DriverTelemetryState {
    wasInsideHub: boolean;
    outsideHubCount: number;
    // Rastreamento de chegada
    arrivalConfirmationCount: number;
}

interface Params {
    config: SystemConfig;
    setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
}

const HUB_EXIT_RADIUS_KM = 0.05; // 50 metros
const CLIENT_ARRIVAL_RADIUS_KM = 0.08; // 80 metros (margem para sinal GPS instável)
const CONFIRMATION_THRESHOLD = 2; // Leituras consecutivas para confirmar evento

export const useHubExitDetection = ({ config, setOrders }: Params) => {
    const telemetryStates = useRef<Record<string, DriverTelemetryState>>({});

    const handleHubExitTelemetry = useCallback((driverId: string, lat: number, lng: number, activeOrders: Order[]) => {
        const hubLat = config.businessLat;
        const hubLng = config.businessLng;

        if (!hubLat || !hubLng) return;

        // 1. Inicialização do Estado do Motorista
        if (!telemetryStates.current[driverId]) {
            telemetryStates.current[driverId] = {
                wasInsideHub: true,
                outsideHubCount: 0,
                arrivalConfirmationCount: 0
            };
        }

        const state = telemetryStates.current[driverId];

        // 2. DETECÇÃO DE SAÍDA DO HUB (Lógica Original)
        const distToHub = DistanceService.getDistanceSync(lat, lng, hubLat, hubLng);
        const isOutsideHub = distToHub > HUB_EXIT_RADIUS_KM;

        if (isOutsideHub) {
            state.outsideHubCount++;
            if (state.wasInsideHub && state.outsideHubCount >= CONFIRMATION_THRESHOLD) {
                // Marca saída do Hub se ainda não marcada
                setOrders(prev => prev.map(o => {
                    if (o.driverId === driverId && o.status === 'assigned' && o.pickupConfirmed && !o.departedHubAt) {
                        return { ...o, departedHubAt: Date.now() };
                    }
                    return o;
                }));
                state.wasInsideHub = false;
            }
        } else {
            state.wasInsideHub = true;
            state.outsideHubCount = 0;
        }

        // 3. DETECÇÃO DE CHEGADA NO CLIENTE (NOVO v4.9)
        // Monitora apenas pedidos que estão 'dispatched' (na rua) e ainda não chegaram
        const missions = activeOrders.filter(o => o.driverId === driverId && o.status === 'dispatched' && !o.arrivedAt);

        if (missions.length > 0) {
            // Prioriza o próximo da sequência (menor routeSequence)
            const target = missions.sort((a, b) => (a.routeSequence || 0) - (b.routeSequence || 0))[0];
            const targetLat = target.reconLat || target.lat;
            const targetLng = target.reconLng || target.lng;

            if (targetLat && targetLng) {
                const distToClient = DistanceService.getDistanceSync(lat, lng, targetLat, targetLng);

                if (distToClient < CLIENT_ARRIVAL_RADIUS_KM) {
                    state.arrivalConfirmationCount++;
                    if (state.arrivalConfirmationCount >= CONFIRMATION_THRESHOLD) {
                        setOrders(prev => prev.map(o => o.id === target.id ? { ...o, arrivedAt: Date.now() } : o));
                        state.arrivalConfirmationCount = 0; // Reset para o próximo
                    }
                } else {
                    state.arrivalConfirmationCount = 0;
                }
            }
        }

    }, [config.businessLat, config.businessLng, setOrders]);

    return { handleHubExitTelemetry };
};
